import { AlertCircle, CheckCircle2 } from "lucide-react";
import type { InputHTMLAttributes, ReactNode, SelectHTMLAttributes, TextareaHTMLAttributes } from "react";
import type { FormState } from "@/lib/auth/validation";
import { cn } from "@/lib/cn";

/** Field look without a width, for inline controls (filters, bulk bars). */
export const inputBase =
  "rounded-xl border border-white/10 bg-white/[0.04] px-3.5 text-[15px] text-fg outline-none transition-colors placeholder:text-faint hover:border-white/15 focus:border-brand/60 focus:bg-white/[0.06] aria-[invalid=true]:border-red-400/60 disabled:opacity-50";
export const inputClass = `w-full ${inputBase}`;

function Label({ htmlFor, children, hint }: { htmlFor: string; children: ReactNode; hint?: ReactNode }) {
  return (
    <div className="mb-1.5 flex items-baseline justify-between gap-3">
      <label htmlFor={htmlFor} className="text-[13px] font-medium text-soft">
        {children}
      </label>
      {hint && <span className="text-[12px] text-faint">{hint}</span>}
    </div>
  );
}

export function FieldError({ id, message }: { id: string; message?: string }) {
  if (!message) return null;
  return (
    <p id={id} className="mt-1.5 text-[12.5px] text-red-300">
      {message}
    </p>
  );
}

type TextFieldProps = InputHTMLAttributes<HTMLInputElement> & { name: string; label: ReactNode; error?: string; hint?: ReactNode; help?: ReactNode };

export function TextField({ name, label, error, hint, help, className, id, ...props }: TextFieldProps) {
  const fieldId = id ?? `f-${name}`;
  return (
    <div className={className}>
      <Label htmlFor={fieldId} hint={hint}>
        {label}
      </Label>
      <input
        id={fieldId}
        name={name}
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? `${fieldId}-err` : undefined}
        className={cn(inputClass, "h-11")}
        {...props}
      />
      {help && !error && <p className="mt-1.5 text-[12px] text-faint">{help}</p>}
      <FieldError id={`${fieldId}-err`} message={error} />
    </div>
  );
}

type TextAreaProps = TextareaHTMLAttributes<HTMLTextAreaElement> & { name: string; label: ReactNode; error?: string; hint?: ReactNode; help?: ReactNode };

export function TextArea({ name, label, error, hint, help, className, id, rows = 4, ...props }: TextAreaProps) {
  const fieldId = id ?? `f-${name}`;
  return (
    <div className={className}>
      <Label htmlFor={fieldId} hint={hint}>
        {label}
      </Label>
      <textarea
        id={fieldId}
        name={name}
        rows={rows}
        aria-invalid={error ? true : undefined}
        className={cn(inputClass, "resize-y py-2.5 leading-relaxed")}
        {...props}
      />
      {help && !error && <p className="mt-1.5 text-[12px] text-faint">{help}</p>}
      <FieldError id={`${fieldId}-err`} message={error} />
    </div>
  );
}

type SelectProps = SelectHTMLAttributes<HTMLSelectElement> & {
  name: string;
  label: ReactNode;
  options: { value: string; label: string }[];
  error?: string;
  help?: ReactNode;
};

export function SelectField({ name, label, options, error, help, className, id, ...props }: SelectProps) {
  const fieldId = id ?? `f-${name}`;
  return (
    <div className={className}>
      <Label htmlFor={fieldId}>{label}</Label>
      <select id={fieldId} name={name} aria-invalid={error ? true : undefined} className={cn(inputClass, "h-11 cursor-pointer appearance-none bg-[length:16px] bg-[right_12px_center] bg-no-repeat pr-9 [background-image:url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%238e939d' stroke-width='2'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E\")]")} {...props}>
        {options.map((o) => (
          <option key={o.value} value={o.value} className="bg-surface">
            {o.label}
          </option>
        ))}
      </select>
      {help && !error && <p className="mt-1.5 text-[12px] text-faint">{help}</p>}
      <FieldError id={`${fieldId}-err`} message={error} />
    </div>
  );
}

export function CheckboxField({
  name,
  label,
  error,
  defaultChecked,
  className,
  value,
}: {
  name: string;
  label: ReactNode;
  error?: string;
  defaultChecked?: boolean;
  className?: string;
  value?: string;
}) {
  const fieldId = `f-${name}`;
  return (
    <div className={className}>
      <label htmlFor={fieldId} className="flex cursor-pointer items-start gap-3 text-[14px] leading-snug text-soft">
        <input
          id={fieldId}
          name={name}
          type="checkbox"
          value={value}
          defaultChecked={defaultChecked}
          aria-invalid={error ? true : undefined}
          className="mt-0.5 size-[18px] shrink-0 cursor-pointer rounded accent-brand"
        />
        <span>{label}</span>
      </label>
      <FieldError id={`${fieldId}-err`} message={error} />
    </div>
  );
}

export function FormAlert({ state }: { state: FormState }) {
  if (state.error) {
    return (
      <div role="alert" className="flex items-start gap-2.5 rounded-xl border border-red-400/25 bg-red-500/10 px-3.5 py-3 text-[14px] text-red-200">
        <AlertCircle size={18} className="mt-px shrink-0" />
        {state.error}
      </div>
    );
  }
  if (state.message) {
    return (
      <div role="status" className="flex items-start gap-2.5 rounded-xl border border-emerald-400/25 bg-emerald-500/10 px-3.5 py-3 text-[14px] text-emerald-200">
        <CheckCircle2 size={18} className="mt-px shrink-0" />
        {state.message}
      </div>
    );
  }
  return null;
}
